import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import FlightStepper from "@/components/site/FlightStepper";
import DatePicker from "@/components/site/DatePicker";
import NationalityAutocomplete from "@/components/site/NationalityAutocomplete";
import PhoneInput from "@/components/site/PhoneInput";
import { draftStore, computeTotal } from "@/lib/flightDraft";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { validatePassenger, validateContact } from "@/lib/paxValidation";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, User } from "lucide-react";

function emptyPax() {
  return {
    title: "Mr", first_name: "", last_name: "", dob: "", gender: "Male",
    nationality: "", passport_number: "", passport_expiry: "",
  };
}

export default function FlightTravelers() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { format: fmt } = useCurrency();

  // Lazy-initialise from the session draft so we don't mutate state inside an effect.
  const [draft] = useState(() => draftStore.get());

  const [travelers, setTravelers] = useState(() => {
    const d = draftStore.get();
    if (!d) return [];
    const count = Number(d.search?.passengers || 1);
    const existing = d.travelers || [];
    return Array.from({ length: count }, (_, i) => existing[i] || emptyPax());
  });

  const [contact, setContact] = useState(() => {
    const d = draftStore.get();
    return d?.contact || { name: "", email: "", phone: "" };
  });
  // Track whether the user manually edited a contact field so auto-fill doesn't clobber input.
  const [contactTouched, setContactTouched] = useState({ name: false, email: false, phone: false });

  const [paxErrors, setPaxErrors] = useState(() => {
    const d = draftStore.get();
    const count = Number(d?.search?.passengers || 1);
    return Array.from({ length: count }, () => ({}));
  });
  const [contactErrors, setContactErrors] = useState({});

  // Guard: redirect if unauthenticated or no draft (wait for auth to finish loading).
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!draft || !draft.selections?.length) { navigate("/flights"); }
  }, [authLoading, user, draft, navigate]);

  // Derive displayed contact: prefer typed value, fall back to user profile when not touched.
  const displayContact = useMemo(() => ({
    name: contactTouched.name ? contact.name : (contact.name || user?.name || ""),
    email: contactTouched.email ? contact.email : (contact.email || user?.email || ""),
    phone: contactTouched.phone ? contact.phone : (contact.phone || ""),
  }), [contact, contactTouched, user]);

  const travelDate = useMemo(
    () => draft?.search?.segments?.[0]?.date || "",
    [draft]
  );
  const today = new Date().toISOString().slice(0, 10);

  if (!draft) return null;

  const upd = (i, key, val) => {
    const next = [...travelers];
    next[i] = { ...next[i], [key]: val };
    setTravelers(next);
    // clear that field's error live
    if (paxErrors[i]?.[key]) {
      const e = [...paxErrors];
      const copy = { ...e[i] };
      delete copy[key];
      e[i] = copy;
      setPaxErrors(e);
    }
  };

  const submit = (e) => {
    e.preventDefault();

    const nextPaxErrors = travelers.map((t) => validatePassenger(t, travelDate, "ADULT").errors);
    const { errors: cErrs } = validateContact(displayContact);

    setPaxErrors(nextPaxErrors);
    setContactErrors(cErrs);

    const paxOk = nextPaxErrors.every((er) => Object.keys(er).length === 0);
    const contactOk = Object.keys(cErrs).length === 0;

    if (!paxOk || !contactOk) {
      toast.error("Please fix the highlighted fields before continuing.");
      // focus first error
      const idx = nextPaxErrors.findIndex((er) => Object.keys(er).length > 0);
      if (idx >= 0) {
        const card = document.querySelector(`[data-testid="pax-card-${idx}"]`);
        card?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        document.querySelector('[data-testid="contact-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    draftStore.patch({ travelers, contact: displayContact });
    navigate("/flights/ancillaries");
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-white font-heading text-2xl sm:text-3xl font-semibold">Traveler details</h1>
        <FlightStepper current="travelers" />
      </div>

      <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
        {travelers.map((t, i) => {
          const errs = paxErrors[i] || {};
          return (
            <Card key={i} className="p-6" data-testid={`pax-card-${i}`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-teal-50 text-[var(--brand-primary)] flex items-center justify-center"><User className="w-4 h-4" /></div>
                <div>
                  <div className="font-heading font-semibold text-[var(--brand-accent)]">Passenger {i + 1}</div>
                  <div className="text-xs text-slate-500">As per passport, exactly</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-2">
                  <Label>Title</Label>
                  <Select value={t.title} onValueChange={(v) => upd(i, "title", v)}>
                    <SelectTrigger data-testid={`pax-title-${i}`} className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">Mr</SelectItem>
                      <SelectItem value="Ms">Ms</SelectItem>
                      <SelectItem value="Mrs">Mrs</SelectItem>
                      <SelectItem value="Mstr">Mstr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-5">
                  <Label>First name</Label>
                  <Input
                    data-testid={`pax-first-${i}`}
                    value={t.first_name}
                    onChange={(e) => upd(i, "first_name", e.target.value)}
                    className={`h-11 ${errs.first_name ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                  />
                  {errs.first_name && (
                    <div className="text-xs text-red-500 mt-1" data-testid={`pax-first-${i}-error`}>{errs.first_name}</div>
                  )}
                </div>

                <div className="md:col-span-5">
                  <Label>Last name</Label>
                  <Input
                    data-testid={`pax-last-${i}`}
                    value={t.last_name}
                    onChange={(e) => upd(i, "last_name", e.target.value)}
                    className={`h-11 ${errs.last_name ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                  />
                  {errs.last_name && (
                    <div className="text-xs text-red-500 mt-1" data-testid={`pax-last-${i}-error`}>{errs.last_name}</div>
                  )}
                </div>

                <div className="md:col-span-3">
                  <Label>Date of birth</Label>
                  <DatePicker
                    testId={`pax-dob-${i}`}
                    value={t.dob}
                    onChange={(v) => upd(i, "dob", v)}
                    maxDate={today}
                    yearDropdown
                    fromYear={1925}
                    toYear={new Date().getFullYear()}
                    error={errs.dob}
                  />
                </div>

                <div className="md:col-span-3">
                  <Label>Gender</Label>
                  <Select value={t.gender} onValueChange={(v) => upd(i, "gender", v)}>
                    <SelectTrigger data-testid={`pax-gender-${i}`} className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-3">
                  <Label>Nationality</Label>
                  <NationalityAutocomplete
                    testId={`pax-nat-${i}`}
                    value={t.nationality}
                    onChange={(v) => upd(i, "nationality", v)}
                    error={errs.nationality}
                  />
                  {errs.nationality && (
                    <div className="text-xs text-red-500 mt-1" data-testid={`pax-nat-${i}-error`}>{errs.nationality}</div>
                  )}
                </div>

                <div className="md:col-span-3">
                  <Label>Passport number</Label>
                  <Input
                    data-testid={`pax-passport-${i}`}
                    value={t.passport_number}
                    onChange={(e) => upd(i, "passport_number", e.target.value.toUpperCase())}
                    className={`h-11 uppercase ${errs.passport_number ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                  />
                  {errs.passport_number && (
                    <div className="text-xs text-red-500 mt-1" data-testid={`pax-passport-${i}-error`}>{errs.passport_number}</div>
                  )}
                </div>

                <div className="md:col-span-3">
                  <Label>Passport expiry</Label>
                  <DatePicker
                    testId={`pax-passport-expiry-${i}`}
                    value={t.passport_expiry}
                    onChange={(v) => upd(i, "passport_expiry", v)}
                    minDate={today}
                    yearDropdown
                    fromYear={new Date().getFullYear()}
                    toYear={new Date().getFullYear() + 20}
                    error={errs.passport_expiry}
                  />
                </div>
              </div>
            </Card>
          );
        })}

        <Card className="p-6" data-testid="contact-card">
          <h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Contact details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <Label>Name</Label>
              <Input
                data-testid="contact-name"
                value={displayContact.name}
                onChange={(e) => { setContactTouched((t) => ({ ...t, name: true })); setContact({ ...contact, name: e.target.value }); }}
                className="h-11"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={displayContact.email}
                onChange={(e) => {
                  setContactTouched((t) => ({ ...t, email: true }));
                  setContact({ ...contact, email: e.target.value });
                  if (contactErrors.email) setContactErrors({ ...contactErrors, email: undefined });
                }}
                className={`h-11 ${contactErrors.email ? "border-red-400 focus-visible:ring-red-300" : ""}`}
              />
              {contactErrors.email && (
                <div className="text-xs text-red-500 mt-1" data-testid="contact-email-error">{contactErrors.email}</div>
              )}
            </div>
            <div>
              <Label>Phone</Label>
              <PhoneInput
                testId="contact-phone"
                value={displayContact.phone}
                onChange={(v) => {
                  setContactTouched((t) => ({ ...t, phone: true }));
                  setContact({ ...contact, phone: v });
                  if (contactErrors.phone) setContactErrors({ ...contactErrors, phone: undefined });
                }}
                error={contactErrors.phone}
              />
              {contactErrors.phone && (
                <div className="text-xs text-red-500 mt-1" data-testid="contact-phone-error">{contactErrors.phone}</div>
              )}
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-slate-500">Running total</div>
              <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">{fmt(computeTotal({ ...draft, travelers }))}</div>
            </div>
            <Button data-testid="travelers-continue-btn" type="submit" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full h-12 px-6">
              Continue to add-ons <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
